import type { Dayjs } from "dayjs";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { NotesTableTypes } from "../hexagon/adapters/driven/enums";
import dayjs from "dayjs";
import { PickerValue } from "antd-mobile/es/components/picker-view";

// TODO use this constant throughout the app, and make constants for production, localhost, etc.
// Search "cipstaging.circadia.link" in the codebase to find all if/else cascades that use string literals that need to use constants.
export const STAGING_URLS = [
  "cipstaging.circadia.link",
  "cip-beta.circadia.link",
  "cipbeta.circadia.link",
  // "localhost",
];

export const shouldShowMaskToggle = (): boolean => {
  return STAGING_URLS.includes(window.location.hostname);
};

export const extractNameFromEmail = (email: string): string => {
  const atIndex = email.indexOf("@");
  const name = email.substring(0, atIndex);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export enum SelectedDateFilter {
  ALL = 0,
  TODAY = 1,
  YESTERDAY = 2,
  LAST_WEEK = 3,
  LAST_MONTH = 4,
  CUSTOM_RANGE = 5,
  LAST_30_DAYS = 6,
}

export const getPriorityFromScore = (score: number) => {
  if (Math.trunc(score * 100) > 75) return "High";
  return "Moderate";
};

export const SelectedFrequency = {
  DAY_TIME: {
    value: 0,
    label: "Day Time",
    time: "7:00 a.m. - 3:00 p.m.",
    startTime: "07:00:00",
    endTime: "15:00:00",
    enum: "DAY_TIME" as frequencyType,
  },
  AFTERNOON: {
    value: 1,
    label: "Afternoon",
    time: "3:00 p.m. - 11:00 p.m.",
    startTime: "15:00:00",
    endTime: "23:00:00",
    enum: "AFTERNOON" as frequencyType,
  },
  NOCTURNAL: {
    value: 2,
    label: "Nocturnal",
    time: "11:00 p.m. - 7:00 a.m.",
    startTime: "23:00:00",
    endTime: "07:00:00",
    enum: "NOCTURNAL" as frequencyType,
  },
  ALL_DAY: {
    value: 3,
    label: "All Day",
    time: "Active 24 hours",
    startTime: "00:00:00",
    endTime: "23:59:59",
    enum: "ALL_DAY" as frequencyType,
  },
  CUSTOM: {
    value: 4,
    label: "Custom",
    time: "",
    startTime: "00:00:00",
    endTime: "23:59:59",
    enum: "CUSTOM" as frequencyType,
  },
};

export enum SelectedNoteFilter {
  CLINICAL_NOTES = "clinical_notes",
  ORDER_NOTES = "order_notes",
  CALL_DOCUMENT = "call_document",
}

export interface IDates {
  start?: Dayjs;
  end?: Dayjs;
}

export const dateFilterData = ({
  filterType,
  timeProvider,
  customDate,
}: {
  filterType: SelectedDateFilter;
  timeProvider: ITimeProvider;
  customDate?: IDates | null;
}) => {
  switch (filterType) {
    case SelectedDateFilter.ALL:
      return {
        // start of day of minus one month
        start: timeProvider.getDateTime().subtract(1, "month").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.TODAY:
      return {
        // start of day of today
        start: timeProvider.getDateTime().startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.YESTERDAY:
      return {
        // start of day of yesterday
        start: timeProvider.getDateTime().subtract(1, "day").startOf("day"),
        // end of day of yesterday
        end: timeProvider.getDateTime().subtract(1, "day").endOf("day"),
      };
    case SelectedDateFilter.LAST_WEEK:
      return {
        // start of day minus one week of today
        start: timeProvider.getDateTime().subtract(1, "week").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.LAST_MONTH:
      return {
        // start of day minus one month of today
        start: timeProvider
          .getDateTime()
          .subtract(1, "month")
          .startOf("month")
          .startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };

    case SelectedDateFilter.LAST_30_DAYS:
      return {
        // start of day minus one month of today
        start: timeProvider.getDateTime().subtract(30, "days").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };

    case SelectedDateFilter.CUSTOM_RANGE:
      if (customDate?.start && customDate?.end)
        return {
          // start of day minus one month of today
          start: dayjs(customDate?.start),
          // end of day of today
          end: dayjs(customDate?.end),
        };
  }
};

export const getBase64FromUrl = async (
  url: string
): Promise<string | ArrayBuffer | null> => {
  const data = await fetch(url);
  const blob = await data.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

export const getBase64FromFile = async (file: File) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

export const getFileName = (name: string) => {
  const strSpt = name.split(".");
  const ext = strSpt.length > 1 ? strSpt.pop() : "";
  const nameWithoutExt = strSpt.join(".");

  return {
    name: name,
    ext: ext || "",
    nameWithoutExt,
  };
};

export const formatAbsoluteValue = (value = 0, precision = true) => {
  let finalValue;
  const parsedValue = Number(value);
  if (parsedValue > 0) {
    finalValue = precision ? `+${parsedValue.toFixed(1)}` : `+${parsedValue}`;
  } else {
    finalValue = precision ? parsedValue.toFixed(1) : parsedValue;
  }
  return Number(finalValue) === 0 ? 0 : finalValue;
};

export const bedExitFormatValues = (
  item: IDailyReportBedExit,
  type: string
) => {
  let newValue;
  if (type === "change") {
    newValue =
      formatAbsoluteValue(item?.shift_3_exits_change, false) === 0 &&
      item?.shift_3_exits_current === 0
        ? "-"
        : `(${formatAbsoluteValue(item?.shift_3_exits_change, false)})`;
  }

  if (type === "value") {
    newValue =
      item?.shift_3_exits_current === 0 &&
      formatAbsoluteValue(item?.shift_3_exits_change, false) === 0
        ? "-"
        : item?.shift_3_exits_current;
  }
  return newValue;
};

export const getLastCommaFirstName = (firstName: string, lastName: string) => {
  return `${lastName}, ${firstName}`;
};

export const getLastCommaFirstNameFromFullName = (fullNames: string) => {
  const names = fullNames.split(" ");
  if (names.length === 1) return names[0];
  return `${names[names.length - 1]}, ${names.slice(0, -1).join(" ")}`;
};

export const getPatientNotesTitleByTableType = (type: string) => {
  return NotesTableTypes[type as keyof typeof NotesTableTypes];
};

export const byteToMegaByte = (size: number) => {
  return size / 1024 / 1024;
};

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function toCamelCase(str: string, separator: string): string {
  return str
    .toLowerCase()
    .split(separator)
    .map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

export const convert12HourTo24Hour = (
  time: PickerValue[],
  timeProvider: ITimeProvider
) => {
  const [hh, mm, A] = time;
  const hour = (Number(hh) % 12) + (A === "PM" ? 12 : 0);
  return timeProvider
    .dateLibrary()
    .set("hour", hour)
    .set("minute", Number(mm))
    .format("HH:mm:00");
};

export const convert24HourTo12Hour = (
  time: string,
  timeProvider: ITimeProvider
) => {
  const [hour, minute] = time.split(":");
  return timeProvider
    .dateLibrary()
    .set("hour", Number(hour))
    .set("minute", Number(minute))
    .format("hh:mm A");
};

export const arraysContainSameItems = (
  arr1: Array<number | string>,
  arr2: Array<number | string>
) => {
  const copyArr1 = arr1.slice();
  const copyArr2 = arr2.slice();

  copyArr1.sort();
  copyArr2.sort();

  if (copyArr1.length !== copyArr2.length) {
    return false;
  }

  for (let i = 0; i < copyArr1.length; i++) {
    if (copyArr1[i] !== copyArr2[i]) {
      return false;
    }
  }

  return true;
};

export const collatorForSorting = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const currenyFormater = (
  value: number,
  style: "decimal" | "currency" | "percent" | "unit" = "currency",
  fractionDigits = 0
) =>
  new Intl.NumberFormat("en-US", {
    style,
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

export const getNewRecordsByNumberOfDays = <T>(
  arrayOfObject: T[] = [],
  noOfDaysBack: number = 2,
  columnToCompare: keyof T
) => {
  const endDate = dayjs().format("YYYY-MM-DD");
  const startDate = dayjs().subtract(noOfDaysBack, "days").format("YYYY-MM-DD");

  return arrayOfObject.filter((item) =>
    isDateBetween(String(item[columnToCompare]), startDate, endDate)
  );
};

// Write docstring for function below
/**
 * This function checks if the date is between the start and end date.
 * @param date - The date to check
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns boolean
 */
export const isDateBetween = (
  date: string,
  startDate: string,
  endDate: string
) => {
  return dayjs(date)
    .startOf("day")
    .isBetween(
      dayjs(startDate).startOf("day"),
      dayjs(endDate).startOf("day"),
      null,
      "[]"
    );
};

export const maskResidentName = (
  name: string,
  variant: "1" | "2" | "3" = "1"
): string => {
  const isCipStaging = STAGING_URLS.includes(window.location.hostname);
  const maskEnabled = localStorage.getItem("maskPatientNames") !== "false"; // defaults to true if not set

  if (isCipStaging && maskEnabled) {
    if (variant === "1") return "Doe, John";
    if (variant === "2") return "John, Doe";
    if (variant === "3") return "John Doe";
  }

  return name;
};

export const getOperatingSystem = ({
  circadiaEngineProvider,
}: {
  circadiaEngineProvider: ICircadiaEngineProvider;
}) => {
  if (window.electron) {
    return "desktop";
  }

  if (circadiaEngineProvider.isWindowCircadia()) {
    return circadiaEngineProvider.isIOS ? "apple" : "android";
  }

  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("win")) return "windows";

  // Fallback for other Apple devices
  if (userAgent.includes("like mac")) return "apple";

  if (
    userAgent.includes("mac") &&
    !userAgent.includes("iphone") &&
    !userAgent.includes("ipad") &&
    !userAgent.includes("ipod")
  )
    return "macintosh";

  // ChromeOS detection (check before linux since ChromeOS is linux-based)
  if (
    userAgent.includes("cros") ||
    userAgent.includes("chromeos") ||
    (userAgent.includes("chrome") &&
      userAgent.includes("linux") &&
      userAgent.includes("armv7l")) ||
    navigator.platform.toLowerCase().includes("cros") ||
    /\bCrOS\b/.test(navigator.userAgent)
  )
    return "chromeos";

  if (userAgent.includes("linux") && !userAgent.includes("android"))
    return "linux";
  if (userAgent.includes("android")) return "android";
  if (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ipod")
  )
    return "apple";

  return "unknown";
};
